import { selectOrderBookingData, updateBookingDate } from "entities/orders";
import { selectAllBookingTimes } from "entities/toolbar/timeslots/model/selectors";
import { getAllBookingTimeslots, getBookingTimeslots } from "entities/toolbar/timeslots/model/timeslotsThunk";
import { useCallback, useEffect, useState } from "react";
import { useModal } from "shared/lib/modal";
import { useAppDispatch, useAppSelector } from "shared/lib/store"
import { TBox, TSlotSection } from "widgets/allSlotsModal/types";

import morning from 'shared/assets/icons/morning.svg'
import day from 'shared/assets/icons/day.svg'
import evening from 'shared/assets/icons/evening.svg'
import night from 'shared/assets/icons/night.svg'
import { BookingTime, toSelectTimeslot } from "entities/toolbar/timeslots";

export const useAllSlots = (
  currentSlot: string, 
  activeSlot: (bookingTime: BookingTime) => void,
) => {
  const dispatch = useAppDispatch();
  const { openModal, isOpen, closeModal } = useModal();
  const allSlots = useAppSelector(selectAllBookingTimes);
  const { dt_start, duration, participants_count, bypass } = useAppSelector(selectOrderBookingData);

  const [ isDispatched, setIsDispatched ] = useState<boolean>(false);
  const [ slots, setSlots ] = useState<TSlotSection[]>([]);
  const [ boxes, setBoxes ] = useState<TBox[]>([]);
  const [ activeBoxes, setActiveBoxes ] = useState<TBox[]>([{id: 'allDay',name: 'Весь день'}]);
  const [ activeAllSlot, setActiveAllSlot ] = useState<string>(currentSlot);
  const [ isButtonDisabled, setIsButtonDisabled ] = useState<boolean>(true);
  
  const setBoxesAction = useCallback(() => {
    if (boxes.length > 1) return;
    const newBoxes: TBox[] = [
      {
        id: 'allDay',
        name: 'Весь день'
      },
      ...slots.map(el=>{
        return {
          id: el.id,
          name: el.name
        }
      })
    ]

    setBoxes(newBoxes);
  }, [boxes, setBoxes, slots])

  const setSlotsAction = useCallback(() => {

    const morningSlots:TSlotSection = {
      id: 'morning',
      icon: morning,
      name: 'Утро',
      time: '06:00 - 12:00',
      timeslots: [],
      isActive: (activeBoxes.find(el=>el.id==='allDay')||activeBoxes.find(el=>el.id==='morning')) ? true : false,
    }
    const daySlots:TSlotSection = {
      id: 'day',
      icon: day,
      name: 'День',
      time: '12:00 - 18:00',
      timeslots: [],
      isActive: (activeBoxes.find(el=>el.id==='allDay')||activeBoxes.find(el=>el.id==='day')) ? true : false
    }
    const eveningSlots:TSlotSection = {
      id: 'evening',
      icon: evening,
      name: 'Вечер',
      time: '18:00 - 24:00',
      timeslots: [],
      isActive: (activeBoxes.find(el=>el.id==='allDay')||activeBoxes.find(el=>el.id==='evening')) ? true : false
    }
    const nightSlots:TSlotSection = {
      id: 'night',
      icon: night,
      name: 'Ночь',
      time: '00:00 - 06:00',
      timeslots: [],
      isActive: (activeBoxes.find(el=>el.id==='allDay')||activeBoxes.find(el=>el.id==='night')) ? true : false
    }

    for (let index = 0; index < allSlots.length; index++) {
      const slot = allSlots[index];
      const startHours = new Date(slot.slot.dt_start).getHours();

      if (startHours >= 6 && startHours < 12) {
        morningSlots.timeslots.push(slot);
      } else if (startHours >= 12 && startHours < 18) {
        daySlots.timeslots.push(slot);
      } else if (startHours >= 18 && startHours < 24) {
        eveningSlots.timeslots.push(slot);
      } else if (startHours >= 0 && startHours < 6) {
        nightSlots.timeslots.push(slot);
      }
    }

    const slots = [];

    if (morningSlots.timeslots.length) {
      slots.push(morningSlots)
    }
    if (daySlots.timeslots.length) {
      slots.push(daySlots)
    }
    if (eveningSlots.timeslots.length) {
      slots.push(eveningSlots)
    }
    if (nightSlots.timeslots.length) {
      slots.push(nightSlots)
    }

    setSlots(slots);
  }, [activeBoxes, setSlots, allSlots])
  
  const setActiveBoxesAction = (boxes: TBox[]) => {
    const allDayIndex = boxes.findIndex(el=>el.id==='allDay')
    if (allDayIndex === 0) {
      if (boxes.length > 1) {
        boxes.splice(0,1);
      }
    } else if (allDayIndex > 0||!boxes.length) {
      boxes = [{id: 'allDay',name: 'Весь день'}];
    }

    setActiveBoxes(boxes);
  }

  const changeSlot = (bookingTime: BookingTime) => {
    setActiveAllSlot(bookingTime.time);
  }

  const onButtonClick = useCallback(async () => {
    const newSlot = allSlots.find(el=>el.time === activeAllSlot);
    if (!newSlot) return;

    const params = {
      count: participants_count,
      duration,
      dt_slot: newSlot.date,
      bypass,
    }
    await dispatch(getBookingTimeslots(params));
    await dispatch(updateBookingDate({ date: newSlot.date }));
    await dispatch(toSelectTimeslot({slot:newSlot.date}))
    activeSlot(newSlot);
    closeModal();
  }, [closeModal, allSlots, activeSlot, activeAllSlot, participants_count, duration, bypass, dispatch])

  useEffect(() => {
    if (!isDispatched&&isOpen) {
      setIsDispatched(true);
      const params = {
        count: participants_count,
        duration,
        dt_slot: dt_start,
        bypass,
        extra: 1000
      }
      dispatch(getAllBookingTimeslots(params))
    }
  }, [participants_count, duration, dt_start, bypass, isDispatched, isOpen, dispatch])

  useEffect(() => {
    setSlotsAction();
  }, [allSlots, activeBoxes, setSlotsAction])

  useEffect(() => {
    if (!isOpen) {
      setIsDispatched(false);
    }
  }, [isOpen])

  useEffect(() => {
    setBoxesAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots])

  useEffect(() => {
    if (allSlots?.length) {
      if (currentSlot?.trim?.()) {
        setActiveAllSlot(currentSlot);
        setIsButtonDisabled(false);
      } else {
        setActiveAllSlot(allSlots[0].time)
      }
    }
  }, [currentSlot, allSlots, setActiveAllSlot])

  return {
    isOpen,
    slots,
    boxes,
    activeBoxes,
    activeAllSlot,
    isButtonDisabled,
    openModal,
    setActiveBoxesAction,
    changeSlot,
    onButtonClick
  }
}